/**
 * JSON formatter / validator — pure logic.
 *
 * Note: JSON.parse error *messages* differ by engine (V8 includes
 * "...at position N", JavaScriptCore does not), so position extraction is
 * best-effort. The line/column math (`lineCol`) is pure and deterministic.
 */

export type Indent = "2" | "4" | "tab";

export interface JsonError {
  message: string;
  line?: number;
  column?: number;
}

/** 1-based line and column for a character offset in `text`. */
export function lineCol(
  text: string,
  pos: number,
): { line: number; column: number } {
  const clamped = Math.max(0, Math.min(pos, text.length));
  const before = text.slice(0, clamped);
  const line = before.split("\n").length;
  const lastNl = before.lastIndexOf("\n");
  const column = clamped - lastNl;
  return { line, column };
}

export function validateJson(text: string): {
  valid: boolean;
  error?: JsonError;
} {
  if (text.trim() === "") {
    return { valid: false, error: { message: "Empty input" } };
  }
  try {
    JSON.parse(text);
    return { valid: true };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    const match = /position (\d+)/.exec(message);
    if (match) {
      const { line, column } = lineCol(text, Number(match[1]));
      return { valid: false, error: { message, line, column } };
    }
    return { valid: false, error: { message } };
  }
}

export interface JsonResult {
  ok: boolean;
  output?: string;
  error?: JsonError;
}

function indentValue(indent: Indent): string | number {
  return indent === "tab" ? "\t" : Number(indent);
}

/** Pretty-print JSON with the chosen indentation. */
export function formatJson(text: string, indent: Indent): JsonResult {
  const v = validateJson(text);
  if (!v.valid) return { ok: false, error: v.error };
  return {
    ok: true,
    output: JSON.stringify(JSON.parse(text), null, indentValue(indent)),
  };
}

/** Collapse JSON to a single line. */
export function minifyJson(text: string): JsonResult {
  const v = validateJson(text);
  if (!v.valid) return { ok: false, error: v.error };
  return { ok: true, output: JSON.stringify(JSON.parse(text)) };
}
